import { Dialog, Transition } from '@headlessui/react';
import { MinusIcon, PlusIcon } from '@heroicons/react/20/solid';
import axios from 'axios';
import copy from 'copy-to-clipboard';
import Markdown from 'markdown-to-jsx';
import * as pdfjsLib from 'pdfjs-dist';
import 'pdfjs-dist/build/pdf.worker.mjs';
import { ChangeEvent, Fragment, useEffect, useRef, useState, useMemo } from 'react';
import CopySVG from 'src/assets/images/icons/CopySVG';
import SendSVG from 'src/assets/images/icons/SendSVG';
import { SUMMARY_API_URL, STORAGE_LIST_URL, STORAGE_DOWNLOAD_URL } from 'src/utils/apiUrls';
import { getAccessToken } from '../../../utils/token-service';
import { DocumentIcon, XMarkIcon } from '@heroicons/react/20/solid';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import { fileList, File as StoredFile, FileListResponse, fileDelete, fileUpload, fileSummarize, fileDownload, UserInfo } from 'src/pages/user-files/service';
import { useMsal } from '@azure/msal-react';


const options = {
	1: 'Extract key points from the file content that captures its main idea. Tell us what is the file about. Limit the response to FIFTY words ONLY.',
	2: `From the given call transcript of a telecom service provider, extract  call reason, sentiment at the end of the call,  customer's problem resolved? ( Y/N).  Display the result in below format 
CALL REASON :
SENTIMENT AT THE END OF THE CALL:
PROBLEM RESOLVED:  YES/NO`,
};

const summarizeTabContent: React.FC = () => {
	const [responseData, setResponseData] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [selectedOption, setSelectedOption] = useState<string>('option1');
	const [fileContent, setFileContent] = useState<string>('');
	const [promptTextareaContent, setPromptTextareaContent] = useState<string>(options[1]);
	const [isPromptCollapsed, setIsPromptCollapsed] = useState(false); // collapsible prompt area
	const [startPage, setStartPage] = useState(1);
	const [endPage, setEndPage] = useState(1);
	const [showPageRange, setShowPageRange] = useState(false);
	const [allText, setAllText] = useState<string[]>([]);
	const [copyButtonText, setCopyButtonText] = useState('Copy');
	const [validationError, setValidationError] = useState<string | null>(null);
	const pdfTextRef = useRef<HTMLTextAreaElement>(null);
	const inputFileRef = useRef<HTMLInputElement>(null);
	// pdf viewer modal
	const [isFileLoading, setFileLoading] = useState(false);
	const [pdfUrl, setPdfUrl] = useState('');
	const [openPdfView, setOpenPdfView] = useState(false);
	const handleClose = () => setOpenPdfView(false);
	const handleShow = () => setOpenPdfView(true);
	// Secure Storage Integration
	const { instance } = useMsal();
	const accounts = instance.getAllAccounts();

	const [user, setUser] = useState<UserInfo>({
		first_name: accounts[0].name ? accounts[0].name.split(", ")[1] : "",
		last_name: accounts[0].name ? accounts[0].name.split(", ")[0] : "",
		user_email: accounts[0].username,
		user_id: accounts[0].tenantId
	})

	const [storageList, setStorageList] = useState<Array<StoredFile>>([]);
	const [download, setDownload] = useState('');
	const [fileInfo, setFileInfo] = useState<Array<string>>([]);
	const [openStorageView, setOpenStorageView] = useState(false);
	const handleCloseStorage = () => setOpenStorageView(false);
	const handleShowStorage = () => setOpenStorageView(true);

	const fetchFileList = async () => {
		const response = await fileList(user);
		const rawFiles = [];

		for (const file of response.folders_files) {
			if (file.raw_file == "true") {
				console.log("Listing: ", file.file_name, "\nRaw flag:", file.raw_file, "\nExt flag:", file.ext_file, "\nDate:", file.file_updated_date, "\nSize:", file.file_size_kb)
				rawFiles.push(file);
			}

		}
		setStorageList(rawFiles);
	}
	const fetchDownload = async (target: StoredFile) => {
		const download = await fileDownload(target, user);
		return download;
	}
	const gridRef = useRef<AgGridReact>(null);

	const rowData = useMemo(
		() =>
			storageList.map((file, index) => ({
				fileName: file.file_name,
				filePath: file.file_path,
				bucketName: file.bucket_name,
				folderPath: file.folder_path,
				rawFile: file.raw_file,
				extFile: file.ext_file,
				size: file.file_size_kb,
				date: file.file_updated_date,
			})),
		[storageList]
	);

	const FileCellRenderer = (params: any) => {
		return (
			<div className="text-[12px] font-medium mt-1 flex items-center justify-between px-2 py-2 leading-4">
				<button><DocumentIcon width={16} height={16} className="flex-2" /></button><p className="flex-1 items-center justify-between px-2 py-2 leading-4">{params.data.fileName}</p>
			</div>
		)
	}
	const ActionCellRenderer = (params: any) => {

		const handleDownload = async () => {
			setDownload('Loading Download Link...');
			setFileInfo([params.data.fileName, params.data.size]);
			try {
				const downloadURL = await fetchDownload({
					bucket_name: params.data.bucketName,
					folder_path: params.data.folderPath,
					raw_file: params.data.rawFile,
					ext_file: params.data.extFile,
					file_path: params.data.filePath,
					file_name: params.data.fileName,
					file_size_kb: params.data.size,
					file_updated_date: params.data.date,
				})
				setDownload(downloadURL);

				console.log(downloadURL);
				const response = await fetch(downloadURL);
				const downloadedFile = await response.blob();
				console.log(downloadedFile);
				const returnFile = new File([downloadedFile], params.data.fileName);
				console.log(returnFile);

				handleCloseStorage();
				handleFileChange(returnFile);

			}
			catch (error) {
				console.error("Download Error");
				//setErrorText('Error generating Download link. Check that file still exists')
				//errorDialog.current?.showModal();
			}
		}

		return (
			<div className="text-[12px] font-medium mt-1">
				<div className="flex items-center gap-2 text-black">
					<button
						className="flex items-center justify-between px-2 py-2 leading-4 border rounded"
						title="Select this file"
						onClick={handleDownload}
					>
						Select
					</button>
				</div>
			</div>
		);
	};

	const colDefs = useMemo(() => {
		return [
			{
				field: "fileName",
				headerName: "File Name",
				width: 150,
				cellRenderer: FileCellRenderer,
			},
			{ field: "date", headerName: "Last Modified", flex: 1 },
			{ field: "size", headerName: "File Size (KB)", flex: 1 },
			{
				field: "Action",
				headerName: "",
				width: 100,
				editable: false,
				sortable: false,
				suppressChangeDetection: true,
				cellRenderer: ActionCellRenderer,
			},
		];
	}, []);

	const defaultColDef = useMemo(
		() => ({
			sortable: true,
			filter: true,
			wrapText: false,
			autoHeight: true,
			cellStyle: { padding: '8px 14px', wordWrap: 'break-word' },
		}),
		[]
	);



	const handleDropdownChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setSelectedOption(event.target.value);
		let _textareaContent = '';
		switch (event.target.value) {
			case 'option1':
				_textareaContent = options[1];
				break;
			case 'option2':
				_textareaContent = options[2];
				break;
			default:
				_textareaContent = options[1];
				break;
		}
		setPromptTextareaContent(_textareaContent);
		setFileContent('');
		// Reset the input type file value
		const fileInput = inputFileRef.current;
		if (fileInput) {
			fileInput.value = '';
		}
	};

	const handleFileSelect = () => {
		console.log("Loading files");
		fetchFileList();
		handleShowStorage();
	}
	// input file reader
	//const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
	const handleFileChange = (file: File) => {
		//const fileInput = event.target;
		//const file = fileInput.files && fileInput.files[0]; 
		setShowPageRange(false);
		if (file) {
			const reader = new FileReader();
			reader.onload = (e) => {
				const content = e.target?.result as string;
				setFileContent(content);
			};
			console.log(fileContent);
			if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
				reader.readAsText(file);
			} else if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
				reader.readAsText(file);
			} else if (file.type === 'application/json' || file.name.endsWith('.json')) {
				reader.readAsText(file);
			} else if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
				console.log('pdf found!');
				setFileLoading(true);
				// pdf reader code
				const fr = new FileReader();
				fr.readAsDataURL(file);
				fr.onload = () => {
					const result = fr.result as string;
					extractText(result);
				};
				setShowPageRange(true);
			} else {
				console.error('Unsupported file type');
			}
		}
	};

	// read content from uploaded file
	const extractText = async (url: string) => {
		try {
			const pdf = await pdfjsLib.getDocument(url).promise;
			setPdfUrl(url);
			const pages = pdf.numPages;

			const texts: string[] = [];

			for (let i = 1; i <= pages; i++) {
				const page = await pdf.getPage(i);
				const txt = await page.getTextContent();
				const text = txt.items.map((s: any) => s.str).join('');
				texts.push(text);
			}

			let fullText = '';
			for (const text of texts)
				fullText += text;
			setFileContent(fullText);
			
			setAllText(texts);
		} catch (err: any) {
			console.error('Error during extraction:', err.message);
			alert(err.message);
		} finally {
			setFileLoading(false);
		}
	};

	// page range selection
	const handlePageRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const inputValue = e.target.value;

		const validFormat = /^\d{0,1}-\d{0,1}$/;

		if (!validFormat.test(inputValue)) {
			// Prevent entering invalid characters
			e.preventDefault();
		} else {
			// Check the length after typing the second digit
			if (inputValue.length === 3) {
				const [start, end] = inputValue.split('-').map(Number);

				// Ensure start value is less than end and vice versa
				if (!isNaN(start) && !isNaN(end) && start < end) {
					setStartPage(start);
					setEndPage(end);
					setValidationError('');
				} else {
					console.error('Invalid page range: Start must be less than end');
					setValidationError('Invalid page range: Start must be less than end');
				}
			}
		}

		const [start, end] = inputValue.split('-').map(Number);

		if (!isNaN(start) && !isNaN(end)) {
			setStartPage(start);
			setEndPage(end);
		}
	};

	const afterProcess = () => {
		// Perform validation before showing pages
		if (endPage < startPage) {
			setValidationError('End Page must be greater than or equal to Start Page');
			return;
		}
		const selectedText = allText.slice(startPage - 1, endPage).join('\n\n');
		pdfTextRef.current!.value = selectedText;
	};

	useEffect(() => {
		afterProcess();
	}, [allText]);

	// summarize the prompt and output
	const handleSummarise = async () => {
		const accessToken = await getAccessToken();
		setLoading(true);
		let data = {
			prompt: promptTextareaContent,
			message: fileContent,
		};
		console.log(data);

		let config = {
			method: 'post',
			maxBodyLength: Infinity,
			url: SUMMARY_API_URL,
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${accessToken}`,
			},
			data: data,
		};
		axios
			.request(config)
			.then((response) => {
				setResponseData(response.data.output);
			})
			.catch((error) => {
				console.log('Error calling API:', error);
			})
			.finally(() => {
				setLoading(false);
			});
	};
	// copy response on click
	const copyToClipboard = () => {
		if (responseData) {
			const responseDataString = responseData as string;
			const plainText = responseDataString.replace(/(?:__|[*#])|\[(.*?)\]\(.*?\)/g, '$1');
			copy(plainText);
			setCopyButtonText('Copied');
			setTimeout(() => {
				setCopyButtonText('Copy');
			}, 1000);
		}
	};
	return (
		<div className="max-w-2xl">
			<div className="mb-3">
				<div>
					<div className="mb-4">
						<select
							className="block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-1 focus:ring-gray-600 sm:text-sm sm:leading-6"
							value={selectedOption}
							onChange={handleDropdownChange}
						>
							<option value="option1">Summarize file content</option>
							<option value="option2">Summarize call transcript</option>
						</select>
					</div>
					{/* prompt text accordion */}
					<div className="mb-4 border rounded-md border-gray-300 bg-white">
						<div className="cursor-pointer px-3 py-2 sm:px-3" onClick={() => setIsPromptCollapsed(!isPromptCollapsed)}>
							<h4 className="flex items-center gap-2 text-sm font-normal leading-6 text-gray-900">
								Customise prompt
								{isPromptCollapsed ? <MinusIcon width={16} height={16} /> : <PlusIcon width={16} height={16} />}
							</h4>
						</div>

						{isPromptCollapsed && (
							<div className="p-4 border-t">
								<textarea
									rows={4}
									className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
									value={promptTextareaContent}
									placeholder="Enter file content here..."
									onChange={(e) => setPromptTextareaContent(e.target.value)}
								/>
							</div>
						)}
					</div>
					{/* context info */}
					<h2 className="text-base font-semibold leading-7 text-gray-900 mb-4">Context Information</h2>

					<div className="flex text-sm leading-6 text-gray-600 mb-4">
						{/* <label
							htmlFor="fileInput"
							className="relative cursor-pointer rounded-md bg-white font-semibold text-gray-600 focus-within:outline-none hover:text-gray-500"
						>
							<input
								ref={inputFileRef}
								id="fileInput"
								accept=".txt, .csv, .json, .pdf"
								onChange={handleFileChange}
								name="file-upload"
								type="file"
							/>
							<button onClick = {handleFileSelect} >
								Select File
							</button>
						</label> */}

						<button className='inline-flex justify-center rounded-md bg-[#106df6] px-4 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-[#106df6] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2' onClick={handleFileSelect} >
							Select File
						</button>
					</div>

					<div>
						{showPageRange && (
							<div>
								<div className="flex items-center gap-6">
									<div className="w-full flex gap-6 items-center">
										<label htmlFor="page-tange" className="text-sm font-medium leading-6 text-gray-900">
											Select page range
										</label>
										<div className="mt-2 sm:col-span-2 sm:mt-0">
											<input
												type="text"
												onChange={handlePageRangeChange}
												placeholder="e.g. 1-5"
												name="page-tange"
												id="page-tange"
												className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
											/>
										</div>
									</div>
									<div className="flex justify-end gap-6 w-full">
										<button
											type="button"
											onClick={afterProcess}
											className="rounded-md bg-[#f8c246] px-2 py-2 text-sm font-semibold text-black shadow-sm hover:bg-[#f8c246]"
										>
											Show Content
										</button>
										<button
											type="button"
											onClick={handleShow}
											className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
										>
											View PDF
										</button>
									</div>
								</div>
								{validationError && <div className="text-red-500 mt-2 text-[14px]">{validationError}</div>}
							</div>
						)}

						{/* pdf content textarea */}
						<div className={`${showPageRange ? 'block' : 'hidden'}`}>
							<div className={`my-4`} style={{ minHeight: '150px' }}>
								<textarea
									rows={5}
									name="comment"
									id="comment"
									ref={pdfTextRef}
									className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
									defaultValue={''}
									onChange={(e) => setFileContent(e.target.value)}
								/>
							</div>
						</div>

						{/* json, txt and csv content textarea */}
						<div className={`${showPageRange ? 'hidden' : 'block'}`}>
							<div className={`my-4`} style={{ minHeight: '150px' }}>
								<textarea
									rows={5}
									name="comment"
									id="comment"
									className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
									value={fileContent}
									onChange={(e) => setFileContent(e.target.value)}
								/>
							</div>
						</div>

						<button
							onClick={handleSummarise}
							type="button"
							className="inline-flex justify-center rounded-md bg-[#f8c246] px-4 py-2.5 text-sm font-semibold text-black shadow-sm hover:bg-[#f8c246] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
						>
							{loading ? (
								<span role="status">Loading...</span>
							) : (
								<span className="flex gap-2 items-center">
									Submit
									<SendSVG />
								</span>
							)}
						</button>
					</div>
				</div>
				{responseData && (
					<div className="mt-4">
						<div className="flex justify-between mb-4 items-center">
							<h3 className="font-medium">Api Response:</h3>
							<button
								onClick={copyToClipboard}
								type="button"
								className="border px-2 py-1 text-sm font-semibold rounded flex gap-2 items-center"
							>
								<CopySVG />
								{copyButtonText}
							</button>
						</div>
						<div className="responseWrapper p-4 border rounded-md border-gray-300 text-sm">
							{/* <pre className="text-sm whitespace-break-spaces leading-6">{responseData}</pre> */}
							<Markdown>{responseData}</Markdown>
						</div>
					</div>
				)}
			</div>

			{/* pdf preview modal */}
			{pdfUrl && (
				<Transition.Root show={openPdfView} as={Fragment}>
					<Dialog as="div" className="relative z-50" onClose={handleClose}>
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0"
							enterTo="opacity-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100"
							leaveTo="opacity-0"
						>
							<div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
						</Transition.Child>

						<div className="fixed inset-0 z-10 w-screen overflow-y-auto">
							<div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
								<Transition.Child
									as={Fragment}
									enter="ease-out duration-300"
									enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
									enterTo="opacity-100 translate-y-0 sm:scale-100"
									leave="ease-in duration-200"
									leaveFrom="opacity-100 translate-y-0 sm:scale-100"
									leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
								>
									<Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl sm:p-6">
										<div className="flex justify-center mx-auto">
											{isFileLoading ? (
												<p>Loading...</p>
											) : (
												<iframe
													src={pdfUrl}
													width="100%"
													height="600px"
													className="rounded-lg border border-gray-300"
												></iframe>
											)}
										</div>
									</Dialog.Panel>
								</Transition.Child>
							</div>
						</div>
					</Dialog>
				</Transition.Root>
			)}

			{/*Secure Storage modal*/}
			<Transition.Root show={openStorageView} as={Fragment}>
				<Dialog as="div" className="relative z-50" onClose={handleCloseStorage}>
					<Transition.Child
						as={Fragment}
						enter="ease-out duration-300"
						enterFrom="opacity-0"
						enterTo="opacity-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100"
						leaveTo="opacity-0"
					>
						<div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
					</Transition.Child>

					<div className="fixed inset-0 z-10 w-screen overflow-y-auto">
						<div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
							<Transition.Child
								as={Fragment}
								enter="ease-out duration-300"
								enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
								enterTo="opacity-100 translate-y-0 sm:scale-100"
								leave="ease-in duration-200"
								leaveFrom="opacity-100 translate-y-0 sm:scale-100"
								leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
							>
								<Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl sm:p-6">
									<div className="flex justify-center mx-auto">
										<div className="ag-theme-alpine" style={{ height: '70vh' }}>
											<AgGridReact
												ref={gridRef}
												suppressExcelExport={true}
												pagination={true}
												paginationPageSize={20}
												defaultColDef={defaultColDef}
												rowData={rowData}
												columnDefs={colDefs}
												rowSelection={'multiple'}
												editType={'fullRow'}
												pivotPanelShow={'always'}
												reactiveCustomComponents
												enableCellEditingOnBackspace={true}
											/>
										</div>
									</div>
								</Dialog.Panel>
							</Transition.Child>
						</div>
					</div>
				</Dialog>
			</Transition.Root>
		</div>
	);
};

export default summarizeTabContent;
